import React from "react";
import { ArticleLayout } from "../components/ArticleLayout";

const IMG = "/mat-cutting-page.webp";

const FAQS = [
  { q: "Do I need a mat board?", a: "No, not every framed painting or print has a mat board (sometimes called a mount board), but it serves a practical, aesthetic and protective role:", list: [
      "It enhances the presentation of the artwork by adding space and contrast around it",
      "It prevents direct contact between the artwork and the glass, avoiding moisture damage or sticking",
      "It creates a clean border around the artwork that draws the eye inward.",
    ] },
  { q: "How big should my mat be?", a: "As a rule of thumb, a mat border between 50 mm and 100 mm (2-4 inches) suits most artwork, and the border should be noticeably wider than your frame moulding - around 1.5 to 2 times its width - so the piece doesn't look cramped. Larger works and gallery presentations use wider borders. Framer's Bench lets you set a border width and instantly see how it looks, warning you if it's too narrow for your moulding." },
  { q: "How do I calculate mat board cuts and sizes?", a: "You cut the mat to the rebate opening of the frame (the full size the frame will hold), then cut the window opening for the artwork inside it. Framer's Bench gives you both the outer mat cut size and the exact window/border cuts for every mat layer, in mm or inches." },
  { q: "How much should the mat overlap the artwork?", a: "A 3-5 mm overlap on each side is standard: enough to hold the artwork securely without hiding important detail. For bleed or full-view mounting you can reduce it, but never go so small that the print can slip through the window." },
  { q: "Why are my mat corners not clean?", a: "Usually a blunt blade, cutting too fast, or under- and over-cutting the corners. Use a fresh blade for every few cuts, keep steady pressure, and start and stop precisely at your corner marks. A bevel mat cutter with a guide rail gives far cleaner results than a hand-held knife." },
];

const SECTIONS = [
  {
    heading: "The mat has two sizes - get both right",
    paragraphs: [
      "Every mat you cut has an outer size and a window size, and beginners routinely mix them up. The outer size; the cut size, is cut to the frame's rebate opening so it fills the frame. The window is the hole you cut for the artwork to show through.",
      "The window is intentionally a little smaller than the artwork so the mat overlaps and holds it — typically by 3 to 5 millimetres on each side.",
    ],
    tip: "If using a mat cutter with a rigis guide rail, be precise when making the outer cuts, or your window cuts will not be even. If you don't have guide rail cutter, mark your window from the centre outwards, not from the mat edges, so any errors in the outer cut doesn't throw your borders off-balance.",
  },
  {
    heading: "Give the artwork room to breathe",
    paragraphs: [
      "A common novice mistake is a mat border that's too narrow, which makes the artwork feel cramped and the frame heavy. As a guide, keep the mat border wider than the frame moulding - roughly 1.5 to 2 times its width - and lean generous rather than tight.",
      "For prints and photographs, a border of 50-80 mm (2-3 inches) usually looks balanced; larger works and gallery pieces often use more.",
    ],
  },
  {
    heading: "Cutting double and triple mats",
    paragraphs: [
      "Layered mats add depth and a crisp accent line around the artwork. The trick is the reveal — the sliver of each lower mat that peeks out inside the top mat's window.",
      "For each additional layer, the window is cut slightly larger so a consistent reveal of 5-8 mm shows. Keep reveals even on all sides unless you're deliberately weighting the bottom.",
    ],
    list: [
      "Top mat: cut the window to your chosen border.",
      "Under mat: cut its window smaller by the reveal amount (which, somewhat confusingly, means a larger distance from the edge of the mat) so it shows inside the top window.",
      "Third mat: repeat, adding another reveal for a fine accent line.",
    ],
    tip: "When using multiple matboards, cut the outer of each slightly smaller than the one above, and double-sided tape it to the upper mat, so your reference edge is always the top mat. In this way, you ensure the under mats' reveal is even and exact for each side and layer.",
  },
  {
    heading: "Tools and technique for clean bevels",
    paragraphs: [
      "A bevel-cut window — where the cut edge angles back at 45° — is what makes a mat look professional. A dedicated mat cutter with a guide rail and depth-set blade produces repeatable bevels that a craft knife can't match.",
      "Change blades often, cut on a proper cutting board, and keep your pressure even from start to finish.",
    ],
    tip: "Always cut a test window on scrap first when trying a new board thickness — it lets you set the blade depth so you cut through cleanly without scoring the board underneath.",
  },
  {
    heading: "Choosing the right board",
    paragraphs: [
      "Board thickness affects both the look and the depth of your build. Standard 4-ply suits most work; thicker 6- or 8-ply boards give a bold, museum-style bevel but eat into your rebate depth.",
      "For anything you want to last, choose acid-free or conservation board so it won't yellow the artwork over time.",
    ],
  },
];

export default function MatCuttingTips() {
  return (
    <ArticleLayout
      seoTitle="Mat Cutting Tips & Tricks"
      seoDesc="How to cut mat board like a pro: what size to actually cut, calculating window and border sizes, cutting double and triple mats, clean bevels and choosing board."
      path="/mat-cutting-tips"
      image={IMG}
      eyebrow="Mat Cutting Tips & Tricks"
      title="Cut mats that look professional"
      intro="From working out the exact cut sizes to clean 45° bevels and layered reveals - the practical guide to mat cutting for framers of every level."
      sections={SECTIONS}
      faqs={FAQS}
      ctaTitle="Stop wasting matboard. Get perfect cuts the first time."
      ctaText="Exact window, border and bevel positions for single, double and triple mats - framing materials are expensive, and one avoided mistake pays for the tool."
    />
  );
}
